'use client';

import { useUserLogged } from '@/hooks/useUserLogged';
import dynamic from 'next/dynamic';
import TrialSeatGate from '@/components/TrialAccess/TrialSeatGate';
import { MainLayout } from '@/layouts/DashboardLayout/MainContent/main-layout';
import { redirect } from 'next/navigation';
import { usePathname } from 'next/navigation';
import type { NavigationConfigItem } from '@/lib/navigationConfig.types';

// Deferred deliberately. BackfillStatusTracker renders null on every page
// load except the rare one where a backfill is actively running/completed/
// errored (see its own early-return guard), yet it and the team-insights
// query-key hook it pulls in for cache invalidation were shipped in every
// dashboard route's initial bundle.
const BackfillStatusTracker = dynamic(
  () => import('@/components/IngestionStatusTracker/BackfillStatusTracker')
);

interface DashboardLayoutClientProps {
  children: React.ReactNode;
  navConfig: NavigationConfigItem[];
}

export default function DashboardLayoutClient({
  children,
  navConfig,
}: DashboardLayoutClientProps) {
  const { status, user, organization, orgResolutionDone } = useUserLogged();
  const pathname = usePathname();

  if (
    status === 'unauthenticated' &&
    !pathname?.startsWith('/documentation/')
  ) {
    redirect('/login');
  }

  // Dashboard routes require a workspace. A logged-in user whose org
  // resolution finished with nothing (no OrganizationUUID and no legacy
  // OrgName fallback) never completed workspace setup, so send them back to
  // onboarding to resume. orgResolutionDone stays false while the org is
  // still loading and on fetch errors, so a slow or down backend never
  // triggers this.
  //
  // Test the identifying fields, not the object. A user who never had a
  // workspace leaves the org store at its `emptyOrganization` default
  // ({ OrgUUID: '', OrgName: '', ApiKey: '' }), which UserContext copies into
  // context verbatim -- a truthy object, so `!organization` missed the most
  // common no-workspace case and let those users into dashboard routes with an
  // empty OrgUUID. Only an org resolved to literal null was caught before.
  //
  // Also honor user.OrganizationUUID: invite join sets that on the user row
  // before the org store has hydrated (or when setOrganizationByUUID fails
  // soft and leaves emptyOrganization). Bouncing those users to /onboarding
  // forces create-workspace on people who already joined via invite.
  if (
    status === 'authenticated' &&
    user?.UserUUID &&
    orgResolutionDone &&
    !user.OrganizationUUID &&
    !organization?.OrgUUID &&
    !organization?.OrgName &&
    !pathname?.startsWith('/documentation/')
  ) {
    redirect('/onboarding');
  }

  // Says what it is actually waiting on, not "Loading...". This gate is held
  // open by `useSession()`, and until it clears `useOrgUuid()` returns '' so
  // every org-gated query on the page stays undispatched -- a stall here looks
  // from the outside like a page that never issues a request. It also rendered
  // the same ten characters as the root Suspense fallback in `app/layout.tsx`,
  // which is indistinguishable in `document.body.innerText` and is why a
  // minutes-long stall was twice attributed to that boundary instead of here.
  if (status === 'loading' && !user?.UserUUID) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-ds-surface-white"
        role="status"
        aria-live="polite"
        aria-busy="true"
        data-loading-state="dashboard-session"
      >
        <p className="text-sm text-ds-text-subtle">Signing you in&hellip;</p>
      </div>
    );
  }

  return (
    <>
      <BackfillStatusTracker />
      <TrialSeatGate />
      <MainLayout navConfig={navConfig}>
        {children}
      </MainLayout>
    </>
  );
}
